import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, AlertTriangle } from 'lucide-react';

interface ExamTimerProps {
  duration: number; // en minutos
  onTimeUp: () => void;
  isRunning: boolean;
}

const ExamTimer: React.FC<ExamTimerProps> = ({ duration, onTimeUp, isRunning }) => {
  const [timeLeft, setTimeLeft] = useState(duration * 60); // convertir a segundos
  const [isWarning, setIsWarning] = useState(false);
  const [isCritical, setIsCritical] = useState(false);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isRunning && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            onTimeUp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isRunning, timeLeft, onTimeUp]);

  useEffect(() => {
    // Cambiar colores según el tiempo restante
    const minutesLeft = Math.floor(timeLeft / 60);
    const secondsLeft = timeLeft % 60;
    
    if (minutesLeft <= 2 && secondsLeft <= 0) {
      setIsCritical(true);
      setIsWarning(false);
    } else if (minutesLeft <= 5) {
      setIsWarning(true);
      setIsCritical(false);
    } else {
      setIsWarning(false);
      setIsCritical(false);
    }
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getTimerClass = () => {
    if (isCritical) return 'timer-critical';
    if (isWarning) return 'timer-warning';
    return 'timer-normal';
  };

  const getProgressPercentage = () => {
    return ((duration * 60 - timeLeft) / (duration * 60)) * 100;
  };

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className={`exam-timer ${getTimerClass()}`}
    >
      <div className="timer-content">
        <div className="timer-icon">
          {isCritical ? <AlertTriangle size={24} /> : <Clock size={24} />}
        </div>
        
        <div className="timer-info">
          <div className="timer-time">
            {formatTime(timeLeft)}
          </div>
          <div className="timer-label">
            {isCritical ? '¡TIEMPO CRÍTICO!' : isWarning ? 'Poco tiempo' : 'Tiempo restante'}
          </div>
        </div>
      </div>

      <div className="timer-progress">
        <div 
          className="timer-progress-bar"
          style={{ width: `${getProgressPercentage()}%` }}
        />
      </div>

      {isCritical && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ repeat: Infinity, duration: 0.5 }}
          className="timer-pulse"
        />
      )}
    </motion.div>
  );
};

export default ExamTimer;
